import type { ConversionStatus } from './Status'
import type { SaqGradeStatus } from './saq/SaqGrade'
import type { GradingStatus, AttemptStatus } from './TestAttempt'
import type { CheckStatus, TestCheckGroupCounts } from './TestCheck'

export interface BaseSseMessage {
  eventType: string
}

export enum SseMessageType {
  // CONNECTION =================================================
  CONNECTION_ESTABLISHED = 'connection-established',
  CONNECTION_CLOSED = 'connection-closed',

  // APP UPDATES =================================================
  APP_UPDATE_REFRESH = 'app-update-refresh',

  // STATE UPDATES ==============================================
  // Test Attempt Grade
  TEST_ATTEMPT_GRADE_UPDATE = 'test-attempt-grade-update',
  TEST_ATTEMPT_GRADE_STATUS_UPDATE = 'test-attempt-grade-status-update',

  // Assessment Attempt Grade
  ASSESSMENT_ATTEMPT_GRADE_STATUS_UPDATE = 'assessment-attempt-grade-status-update',

  // FUTURE =========================
  // Test Attempt
  TEST_ATTEMPT_UPDATE = 'test-attempt-update',
  TEST_ATTEMPT_CREATE = 'test-attempt-create',
  TEST_ATTEMPT_DELETE = 'test-attempt-delete',

  // Test Attempt Status Update
  TEST_ATTEMPT_STATUS_UPDATE = 'test-attempt-status-update',

  // Test Grade Ranking
  TEST_GRADE_RANKING_INVALIDATION_UPDATE = 'test-grade-ranking-invalidation-update',
  TEST_GRADE_RANKING_UPDATE = 'test-grade-ranking-update',

  // Assessment Grade Rankings
  ASSESSMENT_GRADE_RANKING_INVALIDATION_UPDATE = 'assessment-grade-ranking-invalidation-update',
  ASSESSMENT_GRADE_RANKING_UPDATE = 'assessment-grade-ranking-update',

  // Test Score Distributions
  TEST_SCORE_DISTRIBUTION_UPDATE = 'test-score-distribution-update',

  // Assessment Score Distributions
  ASSESSMENT_SCORE_DISTRIBUTION_UPDATE = 'assessment-score-distribution-update',

  // Test Lock Update
  TEST_LOCK_UPDATE = 'test-lock-update',

  // Assessment Lock Update
  ASSESSMENT_LOCK_UPDATE = 'assessment-lock-update',

  // Assessment Test Connections Update
  ASSESSMENT_TEST_CONNECTIONS_UPDATE = 'assessment-test-connections-update',

  // Sequence Blueprint
  SEQUENCE_BLUEPRINT_COMPLETION_UPDATE = 'sequence-blueprint-completion-update',

  // Event Blueprint
  EVENT_BLUEPRINT_COMPLETION_UPDATE = 'event-blueprint-completion-update',

  // Sequence Events
  SEQUENCE_EVENT_CREATED = 'sequence-event-created',
  SEQUENCE_EVENT_UPDATED = 'sequence-event-updated',

  // Context Files
  CONTEXT_FILE_STATUS_UPDATE = 'context-file-status-update',

  // Interaction Files
  INTERACTION_FILE_STATUS_UPDATE = 'interaction-file-status-update',

  // File Submission Batch
  FILE_SUBMISSION_BATCH_UPDATE = 'file-submission-batch-update',
  TEST_SAMPLE_RUN_UPDATE = 'test-sample-run-update',
  TEST_SAMPLE_PASS_UPDATE = 'test-sample-pass-update',
  TEST_SAMPLE_SCRIPT_UPDATE = 'test-sample-script-update',

  // Test Check Status Update (mirrors studio-api `SseResponseType`)
  TEST_CHECK_STATUS_UPDATE = 'test-check-status-update',

  // Standalone question grade status (mirrors studio-api `SseResponseType`)
  SAQ_GRADE_STATUS_UPDATE = 'saq-grade-status-update',

  // Candidate SSE Events (dot-notation — matches backend strings)
  EVENT_ATTEMPT_STARTED = 'eventAttempt.started',
  EVENT_ATTEMPT_COMPLETED = 'eventAttempt.completed',
  SEQUENCE_ATTEMPT_STARTED = 'sequenceAttempt.started',
  SEQUENCE_ATTEMPT_COMPLETED = 'sequenceAttempt.completed',
  TEST_ATTEMPT_GRADE_GRADED = 'testAttemptGrade.graded',
  INTERACTION_FILE_STATUS_UPDATED = 'interaction.fileStatusUpdated',
}

export interface BaseStateUpdateMessage extends BaseSseMessage {
  eventType: SseMessageType
  id: string
}

export interface TestAttemptGradeUpdateMessage extends BaseStateUpdateMessage {
  eventType: SseMessageType.TEST_ATTEMPT_GRADE_UPDATE
  id: string // testAttemptGrade.id
  data: {
    testId: string
  }
}

export interface TestAttemptGradeStatusUpdateMessage
  extends BaseStateUpdateMessage {
  eventType: SseMessageType.TEST_ATTEMPT_GRADE_STATUS_UPDATE
  id: string // testAttemptGrade.id
  data: {
    testId: string
    status: GradingStatus
  }
}

export interface TestAttemptStatusUpdateMessage extends BaseStateUpdateMessage {
  eventType: SseMessageType.TEST_ATTEMPT_STATUS_UPDATE
  id: string // testAttempt.id
  data: {
    testId: string
    status: AttemptStatus
    batchId?: string
    uploadedCount?: number
    totalCount?: number
  }
}

export interface AssessmentAttemptGradeUpdateMessage
  extends BaseStateUpdateMessage {
  eventType: SseMessageType.ASSESSMENT_ATTEMPT_GRADE_STATUS_UPDATE
  id: string // assessmentAttemptGrade.id
  data: {
    assessmentId: string
    status: GradingStatus
  }
}

export interface TestGradeRankingInvalidationUpdateMessage
  extends BaseStateUpdateMessage {
  eventType: SseMessageType.TEST_GRADE_RANKING_INVALIDATION_UPDATE
  id: string // testGradeRanking.id
  data: {
    testId: string
    isValid: boolean
  }
}

export interface TestGradeRankingUpdateMessage extends BaseStateUpdateMessage {
  eventType: SseMessageType.TEST_GRADE_RANKING_UPDATE
  id: string // testGradeRanking.id
  data: {
    testId: string
  }
}

export interface AssessmentGradeRankingInvalidationUpdateMessage
  extends BaseStateUpdateMessage {
  eventType: SseMessageType.ASSESSMENT_GRADE_RANKING_INVALIDATION_UPDATE
  id: string // assessmentGradeRanking.id
  data: {
    assessmentId: string
    isValid: boolean
  }
}

export interface AssessmentGradeRankingUpdateMessage
  extends BaseStateUpdateMessage {
  eventType: SseMessageType.ASSESSMENT_GRADE_RANKING_UPDATE
  id: string // assessmentGradeRanking.id
  data: {
    assessmentId: string
  }
}

export interface TestScoreDistributionUpdateMessage
  extends BaseStateUpdateMessage {
  eventType: SseMessageType.TEST_SCORE_DISTRIBUTION_UPDATE
  id: string // testScoreDistribution.id
  data: {
    testId: string
    lowerBound: number
    upperBound: number
    count: number
  }
}

export interface AssessmentScoreDistributionUpdateMessage
  extends BaseStateUpdateMessage {
  eventType: SseMessageType.ASSESSMENT_SCORE_DISTRIBUTION_UPDATE
  id: string // assessmentScoreDistribution.id
  data: {
    assessmentId: string
    lowerBound: number
    upperBound: number
    count: number
  }
}

export interface TestLockUpdateMessage extends BaseStateUpdateMessage {
  eventType: SseMessageType.TEST_LOCK_UPDATE
  id: string // test.id
  data: {
    lockedBy: string | null
    lockedAt: string | null
  }
}

export interface AssessmentLockUpdateMessage extends BaseStateUpdateMessage {
  eventType: SseMessageType.ASSESSMENT_LOCK_UPDATE
  id: string // assessment.id
  data: {
    lockedBy: string | null
    lockedAt: string | null
  }
}

export interface AppUpdateMessage extends BaseSseMessage {
  eventType: SseMessageType.APP_UPDATE_REFRESH
}

export interface AssessmentTestConnectionsUpdateMessage
  extends BaseStateUpdateMessage {
  eventType: SseMessageType.ASSESSMENT_TEST_CONNECTIONS_UPDATE
  id: string // assessment.id
  data: {
    assessmentId: string
  }
}

export interface SequenceBlueprintCompletionUpdateMessage
  extends BaseStateUpdateMessage {
  eventType: SseMessageType.SEQUENCE_BLUEPRINT_COMPLETION_UPDATE
  id: string // sequenceBlueprint.id
  data: {
    sequenceId?: string
  }
}

export interface EventBlueprintCompletionUpdateMessage
  extends BaseStateUpdateMessage {
  eventType: SseMessageType.EVENT_BLUEPRINT_COMPLETION_UPDATE
  id: string // eventBlueprint.id
  data: {
    eventId?: string
    sequenceId?: string
  }
}

export interface SequenceEventCreatedMessage extends BaseStateUpdateMessage {
  eventType: SseMessageType.SEQUENCE_EVENT_CREATED
  id: string // event.id
  data: {
    sequenceId: string
  }
}

export interface SequenceEventUpdatedMessage extends BaseStateUpdateMessage {
  eventType: SseMessageType.SEQUENCE_EVENT_UPDATED
  id: string // event.id
  data: {
    sequenceId: string
  }
}

export interface ContextFileStatusUpdateMessage extends BaseStateUpdateMessage {
  eventType: SseMessageType.CONTEXT_FILE_STATUS_UPDATE
  id: string // contextFile.id
  data: {
    status: ConversionStatus
    error?: string
  }
}

export interface InteractionFileStatusUpdateMessage
  extends BaseStateUpdateMessage {
  eventType: SseMessageType.INTERACTION_FILE_STATUS_UPDATE
  id: string // interactionFile.id
  data: {
    interactionId?: string
    status: ConversionStatus
    error?: string
    markdownFileKey?: string
  }
}

export interface EventAttemptStartedMessage extends BaseStateUpdateMessage {
  eventType: SseMessageType.EVENT_ATTEMPT_STARTED
  id: string // eventAttempt.id
  data: {
    eventId: string
  }
}

export interface EventAttemptCompletedMessage extends BaseStateUpdateMessage {
  eventType: SseMessageType.EVENT_ATTEMPT_COMPLETED
  id: string // eventAttempt.id
  data: {
    eventId: string
  }
}

export interface SequenceAttemptStartedMessage extends BaseStateUpdateMessage {
  eventType: SseMessageType.SEQUENCE_ATTEMPT_STARTED
  id: string // sequenceAttempt.id
  data: Record<string, any>
}

export interface SequenceAttemptCompletedMessage
  extends BaseStateUpdateMessage {
  eventType: SseMessageType.SEQUENCE_ATTEMPT_COMPLETED
  id: string // sequenceAttempt.id
  data: Record<string, any>
}

export interface TestAttemptGradeGradedMessage extends BaseStateUpdateMessage {
  eventType: SseMessageType.TEST_ATTEMPT_GRADE_GRADED
  id: string // testAttemptGrade.id
  data: {
    testAttemptId: string
  }
}

export interface InteractionFileStatusUpdatedMessage
  extends BaseStateUpdateMessage {
  eventType: SseMessageType.INTERACTION_FILE_STATUS_UPDATED
  id: string // interactionFile.id
  data: {
    status: ConversionStatus
    error?: string
    markdownFileKey?: string
  }
}

export interface FileSubmissionBatchUpdateMessage
  extends BaseStateUpdateMessage {
  eventType: SseMessageType.FILE_SUBMISSION_BATCH_UPDATE
  id: string // batchId
  data: {
    totalCount: number
    successfulCount: number
    skippedCount: number
    failedCount: number
  }
}

// SAMPLE SCRIPTS (synthetic sample grading) --------------------------------
export interface TestSampleRunUpdateMessage extends BaseStateUpdateMessage {
  eventType: SseMessageType.TEST_SAMPLE_RUN_UPDATE
  id: string // runId
  data: {
    testId: string
    kind: string
    status: string
    completedPasses: number
    failedCount: number
    totalPasses: number
    error?: string
  }
}

export interface TestSamplePassUpdateMessage extends BaseStateUpdateMessage {
  eventType: SseMessageType.TEST_SAMPLE_PASS_UPDATE
  id: string // passId
  data: {
    testId: string
    runId: string
    status: string
    error?: string
  }
}

export interface TestSampleScriptUpdateMessage extends BaseStateUpdateMessage {
  eventType: SseMessageType.TEST_SAMPLE_SCRIPT_UPDATE
  id: string // sampleScriptId
  data: {
    testId: string
    status: string
    error?: string
    markdownFileKey?: string
  }
}

/**
 * Where a test stands with its quality checks, pushed as a run progresses:
 * on claim, once per group as that group's last evaluation settles, on the
 * final rollup, on error, and by the api's timeout sweep.
 *
 * `groupKey` present means `counts` is that group's; absent means the whole
 * test's. The api always sends `counts`, but it is typed optional so a payload
 * without it is a no-op rather than a crash.
 */
export interface TestCheckStatusUpdateMessage extends BaseStateUpdateMessage {
  eventType: SseMessageType.TEST_CHECK_STATUS_UPDATE
  id: string // test.id
  data: {
    testId: string
    checkStatus: CheckStatus
    groupKey?: string
    counts?: TestCheckGroupCounts
  }
}

/**
 * A standalone question grade moved: requested, picked up, graded or failed,
 * overridden or reverted (after a revert, `id` names the grade that came back).
 * Not sent on an unlock, and not for the empty-answer grade made at ingest. A
 * bulk request sends one per grade, up to 2,000, so a consumer coalesces.
 */
export interface SaqGradeStatusUpdateMessage extends BaseStateUpdateMessage {
  eventType: SseMessageType.SAQ_GRADE_STATUS_UPDATE
  id: string // saqGrade.id
  data: {
    submissionId: string
    questionId: string
    status: SaqGradeStatus
  }
}

export type SseEventMap = {
  [SseMessageType.TEST_ATTEMPT_GRADE_UPDATE]: TestAttemptGradeUpdateMessage
  [SseMessageType.TEST_ATTEMPT_GRADE_STATUS_UPDATE]: TestAttemptGradeStatusUpdateMessage
  [SseMessageType.ASSESSMENT_ATTEMPT_GRADE_STATUS_UPDATE]: AssessmentAttemptGradeUpdateMessage
  [SseMessageType.TEST_ATTEMPT_STATUS_UPDATE]: TestAttemptStatusUpdateMessage
  [SseMessageType.TEST_GRADE_RANKING_INVALIDATION_UPDATE]: TestGradeRankingInvalidationUpdateMessage
  [SseMessageType.TEST_GRADE_RANKING_UPDATE]: TestGradeRankingUpdateMessage
  [SseMessageType.ASSESSMENT_GRADE_RANKING_INVALIDATION_UPDATE]: AssessmentGradeRankingInvalidationUpdateMessage
  [SseMessageType.ASSESSMENT_GRADE_RANKING_UPDATE]: AssessmentGradeRankingUpdateMessage
  [SseMessageType.TEST_SCORE_DISTRIBUTION_UPDATE]: TestScoreDistributionUpdateMessage
  [SseMessageType.ASSESSMENT_SCORE_DISTRIBUTION_UPDATE]: AssessmentScoreDistributionUpdateMessage
  [SseMessageType.TEST_LOCK_UPDATE]: TestLockUpdateMessage
  [SseMessageType.ASSESSMENT_LOCK_UPDATE]: AssessmentLockUpdateMessage
  [SseMessageType.APP_UPDATE_REFRESH]: AppUpdateMessage
  [SseMessageType.ASSESSMENT_TEST_CONNECTIONS_UPDATE]: AssessmentTestConnectionsUpdateMessage
  [SseMessageType.SEQUENCE_BLUEPRINT_COMPLETION_UPDATE]: SequenceBlueprintCompletionUpdateMessage
  [SseMessageType.EVENT_BLUEPRINT_COMPLETION_UPDATE]: EventBlueprintCompletionUpdateMessage
  [SseMessageType.SEQUENCE_EVENT_CREATED]: SequenceEventCreatedMessage
  [SseMessageType.SEQUENCE_EVENT_UPDATED]: SequenceEventUpdatedMessage
  [SseMessageType.CONTEXT_FILE_STATUS_UPDATE]: ContextFileStatusUpdateMessage
  [SseMessageType.INTERACTION_FILE_STATUS_UPDATE]: InteractionFileStatusUpdateMessage
  [SseMessageType.EVENT_ATTEMPT_STARTED]: EventAttemptStartedMessage
  [SseMessageType.EVENT_ATTEMPT_COMPLETED]: EventAttemptCompletedMessage
  [SseMessageType.SEQUENCE_ATTEMPT_STARTED]: SequenceAttemptStartedMessage
  [SseMessageType.SEQUENCE_ATTEMPT_COMPLETED]: SequenceAttemptCompletedMessage
  [SseMessageType.TEST_ATTEMPT_GRADE_GRADED]: TestAttemptGradeGradedMessage
  [SseMessageType.INTERACTION_FILE_STATUS_UPDATED]: InteractionFileStatusUpdatedMessage
  [SseMessageType.FILE_SUBMISSION_BATCH_UPDATE]: FileSubmissionBatchUpdateMessage
  [SseMessageType.TEST_SAMPLE_RUN_UPDATE]: TestSampleRunUpdateMessage
  [SseMessageType.TEST_SAMPLE_PASS_UPDATE]: TestSamplePassUpdateMessage
  [SseMessageType.TEST_SAMPLE_SCRIPT_UPDATE]: TestSampleScriptUpdateMessage
  [SseMessageType.TEST_CHECK_STATUS_UPDATE]: TestCheckStatusUpdateMessage
  [SseMessageType.SAQ_GRADE_STATUS_UPDATE]: SaqGradeStatusUpdateMessage
}
